import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  Permissions,
  service,
  useAuthConfig,
  useIsCurrentPermission,
} from "@theprelude/core";
import {
  AlertIcon,
  Button,
  CheckmarkIcon,
  CloseIcon,
  IconButton,
  Input,
  Loading,
  notifyError,
  PlusIcon,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  TagIcon,
  TrashcanIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const EndpointTags: React.FC<{
  tags: string[] | [];
  endpoint_id: string;
}> = ({ tags, endpoint_id }) => {
  const displayedTag = tags.slice(0, 1).join(", ");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="group flex max-w-max cursor-pointer items-center gap-2">
          <Button
            intent="pill"
            icon={
              <TagIcon className="text-secondary-foreground group-hover:text-primary-foreground" />
            }
            className="hover:drop-shadow-none group-hover:border-stroke-input group-hover:bg-highlight"
          >
            {tags.length > 0 ? (
              <span>{displayedTag}</span>
            ) : (
              <span className="text-secondary-foreground">No tags</span>
            )}
            {tags.length > 1 && <span>+{tags.length - 1}</span>}
          </Button>
          <IconButton
            icon={<PlusIcon className="group-hover:text-primary-foreground" />}
            className="rounded-full border border-stroke-line border-solid bg-primary-background p-1 group-hover:border-stroke-input group-hover:bg-highlight"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start">
        <EndpointTagContent tags={tags} endpoint_id={endpoint_id} />
      </PopoverContent>
    </Popover>
  );
};

const EndpointTagContent: React.FC<{
  tags: string[];
  endpoint_id: string;
}> = ({ tags, endpoint_id }) => {
  const authConfig = useAuthConfig();
  const { data: isAdmin } = useIsCurrentPermission(Permissions.ADMIN);
  const [showInput, setShowInput] = useState<boolean>(false);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);

  const addTag = useMutation({
    mutationFn: async (tagToAdd: string) => {
      const updatedTags = [...tags, tagToAdd].join(",");
      await service(authConfig).detect.updateEndpoint({
        endpoint_id,
        tags: updatedTags,
      });
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to add tag",
        error,
        dismissible: true,
      });
    },
  });
  const removeTag = useMutation({
    mutationFn: async (tagToDelete: string) => {
      if (!tagToDelete) {
        throw new Error("No tag selected to delete");
      }
      const updatedTags = tags.filter((tag) => tag !== tagToDelete).join(",");
      await service(authConfig).detect.updateEndpoint({
        endpoint_id,
        tags: updatedTags,
      });
    },

    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to remove tag",
        error,
        dismissible: true,
      });
    },
    onSettled: async () => {
      setIndexToDelete(null);
    },
  });
  return (
    <div className="w-56">
      <header className="flex items-center gap-2 border-stroke-line border-b p-2">
        <TagIcon />
        <span>{isAdmin ? "Add tags to your host" : "Tags on this host"}</span>
      </header>
      {tags.map((tag, index) => (
        <div
          key={tag}
          className="flex items-center justify-between gap-2 truncate px-2 py-3"
        >
          <span className="truncate">{tag}</span>
          {indexToDelete === index && removeTag.isPending ? (
            <Loading />
          ) : indexToDelete === index && !removeTag.isPending ? (
            <div className="flex items-center gap-2">
              <IconButton
                icon={<CheckmarkIcon />}
                disabled={!isAdmin}
                onClick={() => {
                  removeTag.mutate(tag);
                }}
              />
              <IconButton
                icon={<CloseIcon />}
                disabled={!isAdmin}
                onClick={() => {
                  setIndexToDelete(null);
                }}
              />
            </div>
          ) : (
            <IconButton
              icon={<TrashcanIcon />}
              disabled={!isAdmin}
              onClick={() => {
                setIndexToDelete(index);
              }}
            />
          )}
        </div>
      ))}
      {isAdmin && showInput ? (
        <div className="!p-0 flex items-center gap-2 border-stroke-line border-t">
          <EndpointTagInput
            tags={tags}
            onAdd={addTag.mutate}
            isPending={addTag.isPending}
          />
        </div>
      ) : (
        <div
          role="button"
          className="!p-2 flex cursor-pointer items-center gap-2 border-stroke-line border-t"
          onClick={() => setShowInput(true)}
        >
          <PlusIcon />
          <span>Add a tag</span>
        </div>
      )}
    </div>
  );
};

const validationRegex = /^[\d._a-z-]{1,20}$/;

const tagSchema = z
  .string()
  .min(1, { message: "Must not be empty." })
  .max(20, { message: "Must be 20 or fewer characters long." })
  .regex(validationRegex, {
    message:
      "Must contain only lowercase letters, numbers, dashes, underscores, and/or periods.",
  });

const EndpointTagInput: React.FC<{
  tags: string[];
  onAdd: (tag: string) => void;
  isPending: boolean;
}> = ({ tags, onAdd, isPending }) => {
  const uniqueTagSchema = tagSchema.refine((tag) => !tags.includes(tag), {
    message: "Must be unique. A tag with this name already exists.",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ tag: string }>({
    resolver: zodResolver(z.object({ tag: uniqueTagSchema })),
  });

  const showError = Boolean(errors.tag?.message);

  const submit = (data: { tag: string }) => {
    onAdd(data.tag);
    reset();
  };

  return (
    <Popover open={showError}>
      <PopoverTrigger asChild>
        <form onSubmit={handleSubmit(submit)} className="w-56 p-1">
          <Input
            {...register("tag")}
            placeholder="Name your tag"
            autoFocus
            maxLength={20}
            disabled={isPending}
            after={
              <PopoverAnchor>
                {isPending ? (
                  <Loading />
                ) : showError ? (
                  <AlertIcon className="text-status-yellow" />
                ) : (
                  <IconButton type="submit" icon={<PlusIcon />} />
                )}
              </PopoverAnchor>
            }
          />
        </form>
      </PopoverTrigger>
      <PopoverContent sideOffset={4}>
        <p className="max-w-56 p-2">{errors.tag?.message}</p>
      </PopoverContent>
    </Popover>
  );
};
